import { Box, Button, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { useTranslatedText } from 'hooks/useTranslatedText';
import {
  type CartItem,
  getSubTicketIcon,
  isSeatedItem,
  type LocalCartItem,
} from 'restful/tickets';
import { isPaidAddon } from 'restful/tickets/addon.utils';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import FormattedMoneyInfo from 'components/@tickets/FormattedMoneyInfo';
import SeatingInfoGroup from 'components/@tickets/SeatingInfoGroup';
import TicketPrice from 'components/@tickets/TicketPrice';
import TicketQuantityIcon from 'components/@tickets/TicketQuantityIcon';

import type { TicketInfoChildRow } from './utils';

type Item = Pick<
  CartItem,
  | 'quantity'
  | 'name'
  | 'totalPrice'
  | 'subtotalPrice'
  | 'bookObjects'
  | 'bookObjectsVerbose'
> &
  Pick<LocalCartItem, 'bookObjectsInfo'>;

interface Props {
  item: Item;
  childRows?: Array<TicketInfoChildRow>;
  onDeleteItem?: (item: Item) => void;
}

const TicketInfoChildRows = ({
  childRows,
}: {
  childRows: Array<TicketInfoChildRow>;
}) => {
  const localize = useTranslatedText();

  if (!childRows.length) {
    return null;
  }

  return (
    <Stack
      sx={{
        py: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 3 },
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'grey.800',
      }}
    >
      {childRows.map((row) => {
        const icon = getSubTicketIcon(row);
        const isPaid = isPaidAddon(row.ticketType, row.addonType);
        const label = isPaid
          ? `${localize(row.name)} (${row.quantity ?? 0})`
          : localize(row.name);

        return (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: 24,
            }}
          >
            {icon && (
              <Icon
                name={icon}
                height={16}
                width={16}
                sx={{ color: 'text.primary', flexShrink: 0, mt: -0.5 }}
              />
            )}
            <Typography
              variant="small"
              sx={{
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {label}
            </Typography>
            {isPaid && !!row.totalPrice && (
              <Typography
                variant="small"
                fontWeight="bold"
                color="text.primary"
                sx={{ whiteSpace: 'nowrap' }}
              >
                <FormattedMessage
                  id={`currency.${row.totalPrice.currency.toLowerCase()}.label`}
                />{' '}
                <FormattedMoneyInfo price={row.totalPrice} style="decimal" />
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const TicketInfoCard = ({ item, childRows, onDeleteItem }: Props) => {
  const localize = useTranslatedText();
  const seatingInfo = item.bookObjectsVerbose ?? item.bookObjectsInfo;

  return (
    <BorderStack sx={{ p: 0 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          py: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 3 },
        }}
      >
        <TicketQuantityIcon quantity={item.quantity} />
        <Stack
          sx={{
            flexGrow: 1,
          }}
        >
          <Typography
            variant="small"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {localize(item.name)}
          </Typography>
          <TicketPrice
            discountedPrice={item.totalPrice}
            originalPrice={item.subtotalPrice}
            color="text.secondary"
          />
        </Stack>
        {!!onDeleteItem && (
          <div>
            {isSeatedItem(item?.bookObjectsInfo) ? (
              <Button
                variant="text"
                onClick={() => onDeleteItem(item)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id="event.seating.change_seats" />
              </Button>
            ) : (
              <Button
                sx={{ alignSelf: 'center' }}
                onClick={() => onDeleteItem(item)}
              >
                <Icon name="Trash" />
              </Button>
            )}
          </div>
        )}
      </Box>
      <SeatingInfoGroup
        bookObjectsInfo={seatingInfo}
        hasGroupedTickets={!!childRows?.length}
        sx={{
          px: { xs: 2, md: 3 },
        }}
      />
      {!!childRows?.length && <TicketInfoChildRows childRows={childRows} />}
    </BorderStack>
  );
};

export default TicketInfoCard;
